import { useAppDispatch, useAppSelector } from '_hooks';
import UserService from '_services/UserService';
import { clearCurrentUser, setAccountDetails } from '_store/auth';
import HelperMethods from '_utils/HelperMethods';
import { deleteUser, signOut } from 'aws-amplify/auth';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ROLE_LABEL: Record<string, string> = {
  member: 'Customer',
  moderator: 'Staff',
  staff: 'Staff',
  admin: 'Admin',
  super_admin: 'Admin',
};

const toDateOnly = (value?: string | null): string => {
  if (!value) return '';
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
};

const digitsOnly = (value: string): string =>
  value.replace(/\D/g, '').slice(0, 10);

const isValidDateOfBirth = (value: string): boolean => {
  if (!value) return true;
  return HelperMethods.isPlausibleDateOfBirth(value);
};

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentUser, accountDetails } = useAppSelector(state => state.auth);

  const givenName =
    accountDetails?.givenName || currentUser?.attributes?.given_name || '';
  const familyName =
    accountDetails?.familyName || currentUser?.attributes?.family_name || '';
  const email = accountDetails?.email || currentUser?.attributes?.email || '';
  const roleKey = accountDetails?.roleKey || 'member';

  const savedPhone = digitsOnly(accountDetails?.phoneNumber || '');
  const savedDob = toDateOnly(accountDetails?.dateOfBirth);

  const [phoneNumber, setPhoneNumber] = useState(savedPhone);
  const [dateOfBirth, setDateOfBirth] = useState(savedDob);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    setPhoneNumber(savedPhone);
    setDateOfBirth(savedDob);
  }, [savedPhone, savedDob]);

  useEffect(() => {
    if (status !== 'success') return;
    const timer = setTimeout(() => setStatus('idle'), 3000);
    return () => clearTimeout(timer);
  }, [status]);

  const phoneError =
    phoneNumber.length > 0 && !HelperMethods.isValidPhoneNumber(phoneNumber)
      ? 'Enter a valid 10-digit phone number'
      : '';
  const dobError =
    dateOfBirth && !isValidDateOfBirth(dateOfBirth)
      ? 'Enter a valid date of birth'
      : '';

  const isDirty = phoneNumber !== savedPhone || dateOfBirth !== savedDob;
  const canSave = useMemo(
    () => isDirty && !phoneError && !dobError && !saving,
    [isDirty, phoneError, dobError, saving],
  );

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;

    setSaving(true);
    setStatus('idle');
    setErrorMessage('');

    try {
      const response = (await UserService.updateUserAccount({
        phoneNumber: phoneNumber || null,
        dateOfBirth: dateOfBirth || null,
      })) as typeof accountDetails;

      dispatch(
        setAccountDetails({
          ...accountDetails,
          ...response,
          phoneNumber: response?.phoneNumber ?? '',
          dateOfBirth: toDateOnly(response?.dateOfBirth),
        }),
      );
      setStatus('success');
    } catch (error: any) {
      setStatus('error');
      setErrorMessage(
        error?.detail || error?.message || 'Unable to update your profile.',
      );
    } finally {
      setSaving(false);
    }
  };

  const finishSignOut = async () => {
    try {
      await signOut();
    } catch {
      // The Cognito user may already be gone.
    }
    await dispatch(clearCurrentUser());
    localStorage.removeItem('currentUser');
    localStorage.removeItem('moi_auth_state');
    sessionStorage.clear();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (deleting) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await UserService.deleteAccount();
    } catch (error: any) {
      setDeleting(false);
      setDeleteError(
        error?.detail || error?.message || 'Unable to delete your account.',
      );
      return;
    }

    try {
      await deleteUser();
    } catch {
      // The database account is already closed. Clear the local session either way.
    }

    await finishSignOut();
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="mt-1 text-sm text-gray-600">
          Update your phone number and date of birth
        </p>
      </div>

      <form
        onSubmit={handleSave}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6"
      >
        <div>
          <div className="text-lg font-semibold text-gray-900">
            {givenName} {familyName}
          </div>
          <div className="text-sm text-gray-600">{email}</div>
          <span className="mt-3 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {ROLE_LABEL[roleKey] ?? roleKey}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={e => setPhoneNumber(digitsOnly(e.target.value))}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                phoneError
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="5551234567"
              inputMode="numeric"
              autoComplete="tel"
            />
            {phoneError ? (
              <p className="mt-1 text-sm text-red-600">{phoneError}</p>
            ) : (
              <p className="mt-1 text-sm text-gray-500">10-digit US number</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={e => setDateOfBirth(e.target.value)}
              min={HelperMethods.getMinDateOfBirth()}
              max={HelperMethods.getMaxDateOfBirth()}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                dobError
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
            {dobError && (
              <p className="mt-1 text-sm text-red-600">{dobError}</p>
            )}
          </div>
        </div>

        {status === 'success' && (
          <div className="rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
            Profile updated.
          </div>
        )}
        {status === 'error' && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
          <p className="text-sm text-gray-500">
            To update your name or email, please reach out to Modesto On Ice
            staff.
          </p>
          <button
            type="submit"
            disabled={!canSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      <section className="bg-white rounded-lg shadow-sm border border-red-200 p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Delete account
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Closing your account removes your sign-in. Bookings and waivers stay
            on file at the rink.
          </p>
        </div>
        {deleteError ? (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {deleteError}
          </div>
        ) : null}
        {confirmDelete ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Your account will be closed and you will not be able to sign in.
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={handleDeleteAccount}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setDeleteError('');
              setConfirmDelete(true);
            }}
            className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
          >
            Delete Account
          </button>
        )}
      </section>
    </div>
  );
};

export default Profile;
